import { useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';

/**
 * One click = one action (owner ask 2026-10-21: an impatient double tap must
 * never save a note, task, comment or audit twice). Every button that saves,
 * submits, sends, deletes or uploads goes through this:
 *
 *  - the action is LOCKED the moment it starts — a plain flag, not React
 *    state, so a second tap in the same frame (before the greyed button has
 *    rendered) is refused too;
 *  - while it runs the button is disabled and reads `busyLabel`
 *    ("Saving…", "Submitting…"); other buttons sharing the same lock
 *    (useActionLock) are disabled with their own label;
 *  - when it finishes — or fails — the lock opens again (the action's own
 *    code shows any error, as before).
 *
 * Forms: run the submit handler through `lock.run` (Enter in a field is a
 * second way to submit) and give the submit button `lock` + `busy={lock.busy}`.
 */

/**
 * The tap AFTER a fast action (QA review 2026-10-21): a note saves to the
 * device in milliseconds and its editor closes before the second tap of a
 * double tap arrives — that tap then lands on whatever moved under the
 * finger (the next question's Yes/No, a link). For a moment after an action
 * button is tapped, the next pointer click anywhere on the page is dropped.
 * Keyboard "clicks" (detail 0: Enter/Space) never arm it and never drop.
 */
export const GHOST_TAP_MS = 450;

/**
 * Times are the events' own `timeStamp`s (when the tap HAPPENED), not when
 * it is processed: a second tap queued behind a slow re-render must still
 * count as the same double tap (verify review 2026-10-21).
 */
export function createTapShield() {
  let until = -Infinity;
  return {
    /** An action button was tapped (pointer click). */
    arm(detail: number, at: number) {
      if (detail > 0) until = at + GHOST_TAP_MS;
    },
    /** Should this click be dropped? (true = the second tap of the same double tap) */
    drops(detail: number, at: number): boolean {
      if (detail === 0 || at >= until) return false;
      until = -Infinity; // one drop per arming
      return true;
    },
  };
}

const shield = createTapShield();
let shieldInstalled = false;

/**
 * For a plain button whose tap replaces what is under the finger (a form's
 * "Submit" that opens the review, a Cancel that closes an editor): arms the
 * shield like an ActionButton tap. Pass the click event.
 */
export function armTapShield(e: { detail: number; timeStamp: number }) {
  installShield();
  shield.arm(e.detail, e.timeStamp);
}

function installShield() {
  if (shieldInstalled || typeof document === 'undefined') return;
  shieldInstalled = true;
  // Capture on document runs before React's root listener, so the dropped
  // click never reaches any handler, link or form.
  document.addEventListener(
    'click',
    (e) => {
      if (shield.drops(e.detail, e.timeStamp)) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    true,
  );
}

/**
 * For a button that leaves the page (MSAL sign-in / sign-out redirect): hold
 * the busy look while the browser starts navigating, but never longer than
 * `holdMs` — MSAL's redirect promise only settles after its 30 s navigation
 * timeout, so after the browser's Back button the button would otherwise stay
 * "Signing in…" for half a minute (QA review 2026-10-21). MSAL itself refuses
 * a second redirect while one is in progress.
 */
export function untilLeaving(navigation: Promise<unknown>, holdMs = 4000): Promise<unknown> {
  return Promise.race([navigation, new Promise((r) => setTimeout(r, holdMs))]);
}

export interface Lock {
  readonly locked: boolean;
  /** Runs `fn` unless an action is already running; then resolves to undefined without calling it. */
  run<T>(fn: () => T | Promise<T>): Promise<T | undefined>;
}

/** The lock itself — plain and synchronous, so it holds between two taps of the same frame. */
export function createLock(onChange?: (locked: boolean) => void): Lock {
  let locked = false;
  return {
    get locked() {
      return locked;
    },
    async run<T>(fn: () => T | Promise<T>): Promise<T | undefined> {
      if (locked) return undefined;
      locked = true;
      onChange?.(true);
      try {
        return await fn();
      } finally {
        locked = false;
        onChange?.(false);
      }
    },
  };
}

export interface ActionLock extends Lock {
  /** True while an action runs (state — re-renders the buttons). */
  busy: boolean;
}

/** A lock shared by several buttons (a dialog's Save + Delete) or by a form and its submit button. */
export function useActionLock(): ActionLock {
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const lock = useRef<Lock>();
  if (!lock.current) lock.current = createLock((b) => mounted.current && setBusy(b));
  const l = lock.current;
  return { busy, get locked() { return l.locked; }, run: (fn) => l.run(fn) };
}

type Props = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> & {
  /** The work. May be async; the button stays locked until it settles. */
  onAction?: () => unknown;
  /** Share a lock with other buttons / a form. Default: the button's own. */
  lock?: ActionLock;
  /** Shown while this button's action runs. */
  busyLabel?: ReactNode;
  /** Force the busy look (a form's submit button: `busy={lock.busy}`). */
  busy?: boolean;
  children?: ReactNode;
};

export function ActionButton({ onAction, lock, busyLabel = 'Saving…', busy, disabled, children, type = 'button', ...rest }: Props) {
  const own = useActionLock();
  const l = lock ?? own;
  const [mine, setMine] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(installShield, []);
  const showBusy = busy ?? mine;
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled || l.busy || showBusy}
      aria-busy={showBusy || undefined}
      onClick={(e) => {
        shield.arm(e.detail, e.timeStamp);
        if (!onAction) return; // a form's submit button: the form's onSubmit runs it
        void l
          .run(async () => {
            setMine(true);
            try {
              await onAction();
            } finally {
              if (mounted.current) setMine(false);
            }
          })
          .catch((err: unknown) => console.error('[AuditPlus] action failed', err));
      }}
    >
      {showBusy ? busyLabel : children}
    </button>
  );
}
